import { Card, Box, IconButton, Tooltip, Typography, Button } from "@mui/material";
import { ListContextProvider, useListContext, crudGetAll, ReduxState, useDeleteWithConfirmController, useRefresh } from "ra-core";
import { FC, useCallback, useEffect, useRef } from "react";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Link as RouterLink } from 'react-router-dom'
import { Content } from "~/components/content/Content";
import { FunctionField, TextField } from "~/components/field";
import { Datagrid } from "~/components/list/datagrid";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import Scrollbar from "~/components/Scrollbar";
import { RecPatch } from "~/dataProvider/tools";
import { OdResourcePageProps } from "~/types/resource";
import { useDispatch, useSelector } from "react-redux";
import { sorts } from "~/utils/paramsWithGetMainList";
import { genUrlWithHashid } from "~/utils/hashid";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import Confirm from "~/components/widgets/Confirm";
import { DataAccessRoleResponseDto, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import useLocalListController from "~/components/list/useLocalListController";
import { Location } from 'history'
import { useHasPermit } from "~/authProvider/useHasPermit";
import { MANAGE_DATA_ACCESS_ROLES } from "~/constants";


const listOptions : {sorters: Record<string, (a:ClassificationDto & RecPatch, b:ClassificationDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id - b.id
  }
}

const DataAccessRoleList: FC<OdResourcePageProps> = (props) => {
  const { resource, location } = props
  const pagerDef = (location as Location<{page:number, perPage:number}>)?.state
  const listCtrl = useLocalListController({ resource, basePath: '', perPage: pagerDef?.perPage, filter: { } }, listOptions)

  const { setPage, loaded } = listCtrl
  const pagerRestore = useRef({ restored: false, setPage })
  pagerRestore.current.setPage = setPage

  useEffect(() => {
    if (pagerDef?.page && !pagerRestore.current.restored && loaded) {
      pagerRestore.current.setPage(pagerDef.page)
      pagerRestore.current.restored = true
    }
  }, [pagerDef?.page, loaded])

  const dispatch = useDispatch()
  dispatch(crudGetAll('settings/governance/data-classification', sorts(), {}, 1000))

  return (
    <ListContextProvider value={listCtrl}>
      <DataAccessRoleListBody {...props} />
    </ListContextProvider>
  )
}

type ClassificationDto = SecondaryClassificationResponseDto;

const DataAccessRoleListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATA_ACCESS_ROLES])
  const listCtx = useListContext<DataAccessRoleResponseDto & RecPatch>()
  const { page, perPage } = listCtx

  const classifications = useSelector<ReduxState, {[k:string]: ClassificationDto&RecPatch}>(store => (store.admin.resources['settings/governance/data-classification'].data || {}) as any)

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])
  const recordToDel = useRef<DataAccessRoleResponseDto&RecPatch | null>(null)
  const delHandlers = useDefaultSavedHandlers('Delete Data Access Role')
  delHandlers.onSuccess.merge(refreshLight)
  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: recordToDel.current as any,
    onClick: () => { recordToDel.current = null },
    ...delHandlers,
  })
  
  return (
    <Content 
      pageName="Manage Data Access Roles" 
      resourceName="Data Access Roles" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        hasWritePermit &&
        <Button
          color="primary"
          variant="contained"
          startIcon={
            <AddIcon />
          }
          component={RouterLink}
          to={`/${resource}/create`}
        >
          Add Data Access Role
        </Button>
      }
    >
      
      <Card>
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& .name': { maxWidth: 150, display: 'inline-block', wordBreak: 'break-word' },
                '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
              }}
              {...listCtx}
            >
              <TextField source="name" label="Name" sortable={false} className="name" />
              <TextField source="value" label="Value" sortable={false} className="name" />
              <TextField source="description" label="Description" sortable={false} className="description" />
              <FunctionField 
                source="" 
                label="Data Access" 
                sortable={false} 
                render={(d?: RecPatch & DataAccessRoleResponseDto) => {
                  if (!d) return null;

                  const reducedInfo = 
                    (d.readGrants || []).map(g => [g, 'read' as string] as const).concat(
                      (d.writeGrants || []).map(g => [g, 'write' as string] as const)
                    )
                    .reduce((acc, x) => {
                      const [clsId, rw] = x
                      const cls = classifications[clsId]
                      if (cls) {
                        const entry = acc[cls.id] || (acc[cls.id] = {} as any)
                        entry.name = cls.primaryClassificationText + '-' + (cls.name || 'Default')
                        if (rw === 'read') {
                          entry.read = true
                        } else {
                          entry.write = true
                        }
                      }
                      return acc
                    }, {} as Record<string, {read:boolean, write:boolean, name:string}>);
                  
                  const keys = Object.keys(reducedInfo)
                  const show3lines = (keys.length < 3 ? keys : (keys.length = 3, keys)).map(k => reducedInfo[k])
                  return (
                    <Box
                      sx={{
                        '& .cls-name': {
                          display: 'inline-block',
                          maxWidth: 150,
                          wordBreak: 'keep-all',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          verticalAlign: 'middle',
                          '&:after': {
                            content: '" :"'
                          },
                        }
                      }}
                    >
                      <table>
                        <tbody>
                          {show3lines.map((line, i) => (
                            <tr key={line.name}>
                              <td style={{padding: 0 }}>
                                <Tooltip title={line.name.length > 24 ? line.name : ''}>
                                  <Typography variant="caption" className="cls-name">{line.name}</Typography>
                                </Tooltip>
                              </td>
                              <td style={{padding: 0 }}>
                                <Typography variant="caption">{[...(line.read ? ["Read"] : []), ...(line.write ? ["Write"] : [])].join('/')}</Typography>
                                {Object.keys(reducedInfo).length > 3 && i === 2 &&
                                  <Typography variant="caption" sx={{ pl: 4 }}>
                                    and {Object.keys(reducedInfo).length - 3} more…
                                  </Typography>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )
                }}
              />
              <FunctionField
                label="Actions"
                sortable={false}
                render={(d?: DataAccessRoleResponseDto & RecPatch) => (
                  <>
                    <Tooltip title={hasWritePermit ? "Edit" : "View"}>
                      <IconButton
                        sx={{
                          color: 'text.primary' as any,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        component={RouterLink}
                        to={{
                          pathname: genUrlWithHashid(`/${resource}/$0/show`, d?.id, d?.name),
                          state: { page, perPage },
                        }}
                      >
                        {hasWritePermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {hasWritePermit &&
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={(e:any) => {
                            recordToDel.current = d as any
                            handleDialogOpen(e)
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                  </>
                )}
                textAlign="center"
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      {hasWritePermit &&
        <Confirm
          isOpen={openConfirm}
          title={"Delete Data Access Role"}
          loading={loading}
          onConfirm={handleDelete}
          onClose={handleDialogClose}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Please confirm whether to proceed with the deletion of this
                  {` "${recordToDel.current?.name}" `}
                  Data Access Role.
                </Typography>
              </Box>
            </Box>
          )}
        />}
    </Content>
  )
}

export default DataAccessRoleList;
